const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
const PERIOD_SECONDS = 30;

// Mirrors the backend (pyotp + Python's strict base64.b32decode) so a key
// accepted here yields the same code the server would generate.
const VALID_PADDING_LENGTHS = [0, 1, 3, 4, 6];

export function decodeBase32Secret(value: string): Uint8Array {
  let compact = value.replace(/\s+/g, '').toUpperCase();
  if (!compact) {
    throw new Error('Enter the authenticator setup key.');
  }
  if (compact.length % 8) {
    compact += '='.repeat(8 - (compact.length % 8));
  }
  const normalized = compact.replace(/=+$/, '');
  if (
    !normalized ||
    !VALID_PADDING_LENGTHS.includes(compact.length - normalized.length)
  ) {
    throw new Error('Setup key has an invalid length.');
  }
  let bits = 0;
  let buffer = 0;
  const bytes: number[] = [];
  for (const char of normalized) {
    const index = BASE32_ALPHABET.indexOf(char);
    if (index === -1) {
      throw new Error('Setup key must be Base32 (letters A–Z and digits 2–7).');
    }
    buffer = (buffer << 5) | index;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((buffer >> bits) & 0xff);
    }
  }
  return new Uint8Array(bytes);
}

export async function generateTotp(
  secret: Uint8Array,
  unixSeconds: number,
): Promise<{ code: string; expiresAt: number }> {
  const counter = Math.floor(unixSeconds / PERIOD_SECONDS);
  const message = new DataView(new ArrayBuffer(8));
  message.setUint32(0, Math.floor(counter / 2 ** 32));
  message.setUint32(4, counter >>> 0);
  const key = await crypto.subtle.importKey(
    'raw',
    secret,
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign'],
  );
  const hmac = new Uint8Array(
    await crypto.subtle.sign('HMAC', key, message.buffer),
  );
  const offset = hmac[hmac.length - 1] & 0x0f;
  const binary =
    ((hmac[offset] & 0x7f) << 24) |
    (hmac[offset + 1] << 16) |
    (hmac[offset + 2] << 8) |
    hmac[offset + 3];
  return {
    code: String(binary % 1_000_000).padStart(6, '0'),
    expiresAt: (counter + 1) * PERIOD_SECONDS,
  };
}
